/**
 * @fileoverview Summary statistics KPI cards component
 * @module TechStackAnalysis/components/SummaryStats
 */

import React from 'react';
import { Building2, DollarSign, Layers, Shield, Calendar } from 'lucide-react';

/**
 * Format currency value
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value) => {
  if (!value) return '$0';
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

/**
 * Convert period to friendly label
 * @param {string} period - Period value
 * @returns {string} Friendly label
 */
const getPeriodLabel = (period) => {
  const labels = {
    'last-30-days': '30-Day',
    'last-90-days': '90-Day',
    'last-6-months': '6-Month',
    'last-year': '1-Year',
    'last-2-years': '2-Year',
    'last-3-years': '3-Year',
    'last-5-years': '5-Year',
    'ytd': 'YTD',
    'all-time': 'All-Time'
  };
  return labels[period] || '5-Year';
};

/**
 * Individual stat card component
 */
const StatCard = ({ icon: Icon, label, value, subValue, colorClass }) => (
  <div className={`ts-stat-card ${colorClass}`}>
    <div className="ts-stat-icon">
      <Icon size={18} />
    </div>
    <div className="ts-stat-label">{label}</div>
    <div className="ts-stat-value">{value}</div>
    {subValue && <div className="ts-stat-sub">{subValue}</div>}
  </div>
);

/**
 * Summary stats grid showing 5 KPI cards
 * @param {Object} props
 * @param {Object} props.summary - Summary data object
 * @param {string} props.period - Active time period filter
 */
const SummaryStats = ({ summary, period }) => {
  if (!summary) return null;

  const periodLabel = getPeriodLabel(period);

  const stats = [
    {
      icon: Building2,
      label: 'Total Vendors',
      value: summary.totalVendors || 0,
      subValue: 'Active contracts',
      colorClass: 'ts-stat-card-blue'
    },
    {
      icon: DollarSign,
      label: `${periodLabel} Spend`,
      value: formatCurrency(summary.annualSpend),
      subValue: 'Total procurement',
      colorClass: 'ts-stat-card-green'
    },
    {
      icon: Layers,
      label: 'Stack Coverage',
      value: `${summary.stackCoverage || 0}%`,
      subValue: 'Domains covered',
      colorClass: 'ts-stat-card-purple'
    },
    {
      icon: Shield,
      label: 'Risk Score',
      value: summary.riskScore || 'A',
      subValue: summary.riskScore === 'A' ? 'Low risk' :
                summary.riskScore === 'B' ? 'Moderate' : 'Attention needed',
      colorClass: summary.riskScore === 'A' ? 'ts-stat-card-green' :
                  summary.riskScore === 'B' ? 'ts-stat-card-amber' : 'ts-stat-card-red'
    },
    {
      icon: Calendar,
      label: 'Renewals',
      value: summary.renewals90Days || 0,
      subValue: 'Next 90 days',
      colorClass: summary.renewals90Days > 3 ? 'ts-stat-card-amber' : 'ts-stat-card-blue'
    }
  ];

  return (
    <div className="ts-summary-stats">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

export default SummaryStats;
